import React from "react";
import {ClickAwayListener, Menu, MenuItem} from "@mui/material";
import {useSigma} from "@react-sigma/core";
import {openAllianceGenome, openDepMap, openGeneCards, openUniProt} from "./ExternalLinks";
import {useEntrezMap} from "../hooks/useEntrezMap";
import {useGeneRefs} from "../hooks/useGeneRefs";
import { useTheCellMapContext } from "../contexts/TheCellMapContext";
import { NodeData } from "../types/Types";

// Renders a context menu that appears on right-clicking a network node,
// allowing the user to access external resources (GeneCards, DepMap, Alliance Genome, UniProt)
// for the selected gene. The menu items dynamically use the node's attributes
// to generate the appropriate external links.

const NodeContextMenu = () => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const entrezMap = useEntrezMap();
  const geneRefs = useGeneRefs();
  const {nodeMenuState, closeNodeMenu} = useTheCellMapContext();
  if (!entrezMap || !nodeMenuState) return null;

  const getSymbolByNodeId = (nodeId: string) => {
    const nodeData: NodeData = graph.getNodeAttributes(nodeId) as NodeData;
    return entrezMap[nodeData.e].s;
  };

  const onGeneCardsClick = () => {
    const nodeData: NodeData = graph.getNodeAttributes(nodeMenuState.nodeId) as NodeData;
    openGeneCards(nodeData.e);
    closeNodeMenu();
  };

  const onDepMapClick = () => {
    const sym = getSymbolByNodeId(nodeMenuState.nodeId);
    openDepMap(sym);
    closeNodeMenu();
  };

  const onAllianceClick = () => {
    const nodeData: NodeData = graph.getNodeAttributes(nodeMenuState.nodeId) as NodeData;
    const openedAlliance = openAllianceGenome(nodeData.e, geneRefs);
    if (!openedAlliance) {
      const sym = entrezMap[nodeData.e].s;
      openUniProt(sym);
    }
    closeNodeMenu();
  };

  const onUniProtClick = () => {
    const sym = getSymbolByNodeId(nodeMenuState.nodeId);
    openUniProt(sym);
    closeNodeMenu();
  };

  return (
    <ClickAwayListener onClickAway={closeNodeMenu} mouseEvent="onMouseDown" touchEvent="onTouchStart">
      <Menu
        open={true}
        anchorReference="anchorPosition"
        anchorPosition={{ top: nodeMenuState.anchor.mouseY, left: nodeMenuState.anchor.mouseX }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Make parent element non-interactive to allow pointer events to pass through to Sigma while making sure
        // the actual context menu paper is clickable and interactive.
        slotProps={{
          root: {style: {pointerEvents: 'none'}},
          paper: {style: {pointerEvents: 'auto'}}
        }}
        onClose={closeNodeMenu}
        // Remove modal backdrop so it doesn't intercept pointer events
        // E.g., this allows users to right-click other nodes when a node's context menu is open
        hideBackdrop={true}
      >
        <div>
          <MenuItem onClick={onGeneCardsClick}>
            View GeneCards
          </MenuItem>

          <MenuItem onClick={onDepMapClick}>
            View DepMap
          </MenuItem>

          <MenuItem onClick={onAllianceClick}>
            View Alliance Genome
          </MenuItem>

          <MenuItem onClick={onUniProtClick}>
            View UniProt
          </MenuItem>
        </div>
      </Menu>
    </ClickAwayListener>
  );
};

export default NodeContextMenu;
